// Standalone time clock module.
//
// Works like a physical timeclock on the wall. Two modes:
//   1. Punch — keypad-driven. Enter PIN → see status → tap Clock In or
//      Clock Out → checklist appears → punch is finalized.
//   2. Open — when no PIN entered, shows "Who's clocked in" at this station
//      so the next shift can see who's still on the clock.
//
// The station is determined by the logged-in JWT user (req.user). On a
// shared dispatch terminal this never changes — Maria signs into the
// timeclock with PIN 1234, punches in, walks away from the screen;
// at end of shift she comes back, enters her PIN again, punches out.

import { useEffect, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';

function fmtElapsed(seconds) {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}
function fmtTime(s) {
  if (!s) return '';
  return new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleString();
}

export default function TimeClockPage() {
  const { user } = useAuth();
  const [pin, setPin] = useState('');
  const [pinSession, setPinSession] = useState(null);
  const [status, setStatus] = useState(null);   // { clocked_in, elapsed_seconds, entry_id, on_break }
  const [active, setActive] = useState([]);     // who's currently clocked in here
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState(null); // { scope, entryId }

  // Poll "who's clocked in at this station" every 30s for the bottom panel
  useEffect(() => {
    if (!user) return;
    let cancel = false;
    function poll() {
      api.activeByStation(user.id).then(r => { if (!cancel) setActive(r || []); }).catch(() => {});
    }
    poll();
    const id = setInterval(poll, 30 * 1000);
    return () => { cancel = true; clearInterval(id); };
  }, [user]);

  // Status polling.
  //  - PIN session active → poll the PIN'd user's status
  //  - Individual login (non-station) → poll their own status
  //  - Station with no PIN → show nothing yet (waiting for PIN entry)
  useEffect(() => {
    const shouldFetch = pinSession || (user && !user.is_station_account);
    if (!shouldFetch) { setStatus(null); return; }
    function poll() {
      api.clockStatus().then(setStatus).catch(() => setStatus(null));
    }
    poll();
    const id = setInterval(poll, 30 * 1000);
    return () => clearInterval(id);
  }, [pinSession, user]);

  function press(d) {
    if (busy) return;
    setErr('');
    setPin(p => p.length >= 6 ? p : p + d);
  }
  function back() { setErr(''); setPin(p => p.slice(0, -1)); }
  function clear() { setErr(''); setPin(''); }

  async function submitPin() {
    if (pin.length < 4) return;
    setBusy(true);
    setErr('');
    try {
      const r = await api.pinLogin(pin);
      // Stash session token in localStorage so api.js injects it
      // automatically (used by clockIn/Out/status/checklist calls).
      localStorage.setItem('pinSession', r.session_token);
      setPinSession(r);
      setPin('');
    } catch (e) {
      setErr(e.message || 'Invalid PIN');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  async function endSession() {
    try { await api.pinLogout(); } catch {}
    localStorage.removeItem('pinSession');
    setPinSession(null);
    setStatus(null);
  }

  async function handleClockIn() {
    setBusy(true);
    try {
      const r = await api.clockIn();
      setStatus({ clocked_in: true, entry_id: r.entry.id, clock_in_at: r.entry.clock_in_at, elapsed_seconds: 0 });
      setChecklist({ scope: 'shift_start', entryId: r.entry.id });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleClockOut() {
    setBusy(true);
    try {
      const r = await api.clockOut();
      // End-of-shift checklist is required before fully closing UI
      setChecklist({ scope: 'shift_end', entryId: r.entry.id });
      setStatus({ clocked_in: false });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleBreakStart() {
    setBusy(true);
    try { await api.breakStart(); const s = await api.clockStatus(); setStatus(s); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function handleBreakEnd() {
    setBusy(true);
    try { await api.breakEnd(); const s = await api.clockStatus(); setStatus(s); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="page timeclock-page">
      <div className="page-header">
        <h2>Time Clock {user?.full_name && <span className="muted">· {user.full_name}</span>}</h2>
      </div>

      {/* PUNCH AREA */}
      <div className="timeclock-card">
        {/* Individual logins (Sign Tech, Delivery, etc.) skip the PIN
            keypad entirely — they're already authenticated as themselves. */}
        {!user?.is_station_account ? (
          <PunchPanel
            session={{ active_user: { full_name: user?.full_name || 'You' } }}
            status={status}
            busy={busy}
            onClockIn={handleClockIn}
            onClockOut={handleClockOut}
            onBreakStart={handleBreakStart}
            onBreakEnd={handleBreakEnd}
            onEndSession={null}
            err={err}
            hideEndSession
          />
        ) : !pinSession ? (
          <PinKeypad
            pin={pin} err={err} busy={busy}
            press={press} back={back} clear={clear} submit={submitPin}
          />
        ) : (
          <PunchPanel
            session={pinSession}
            status={status}
            busy={busy}
            onClockIn={handleClockIn}
            onClockOut={handleClockOut}
            onBreakStart={handleBreakStart}
            onBreakEnd={handleBreakEnd}
            onEndSession={endSession}
            err={err}
          />
        )}
      </div>

      {/* WHO'S CLOCKED IN — visible whether or not someone is at the keypad */}
      <div className="timeclock-active">
        <h3>On the clock right now ({active.length})</h3>
        {active.length === 0 && <div className="empty muted">No one is clocked in at this station.</div>}
        {active.map(a => (
          <div key={a.id} className="timeclock-active-row">
            <span className="timeclock-active-name">{a.full_name}</span>
            <span className="timeclock-active-elapsed">{fmtElapsed(a.elapsed_seconds)}</span>
            <span className="timeclock-active-since">since {fmtTime(a.clock_in_at)}</span>
            {a.on_break ? <span className="timeclock-active-break">on break</span> : null}
          </div>
        ))}
      </div>

      {checklist && (
        <ChecklistPunchModal
          scope={checklist.scope}
          entryId={checklist.entryId}
          region={user?.station_region}
          onClose={() => setChecklist(null)}
        />
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function PinKeypad({ pin, err, busy, press, back, clear, submit }) {
  return (
    <>
      <div className="timeclock-prompt">Enter your PIN to punch</div>
      <div className="pin-dots">
        {[0,1,2,3,4,5].map(i => (
          <div key={i} className={`pin-dot ${i < pin.length ? 'pin-dot-filled' : ''}`} />
        ))}
      </div>
      {err && <div className="pin-entry-error">{err}</div>}
      <div className="pin-keypad timeclock-keypad">
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} type="button" className="pin-key" onClick={() => press(String(n))}>{n}</button>
        ))}
        <button type="button" className="pin-key pin-key-aux" onClick={clear}>Clear</button>
        <button type="button" className="pin-key" onClick={() => press('0')}>0</button>
        <button type="button" className="pin-key pin-key-aux" onClick={back}>⌫</button>
      </div>
      <button type="button" className="pin-submit-btn" disabled={pin.length < 4 || busy} onClick={submit}>
        {busy ? 'Checking…' : 'Continue'}
      </button>
    </>
  );
}

function PunchPanel({ session, status, busy, onClockIn, onClockOut, onBreakStart, onBreakEnd, onEndSession, err, hideEndSession }) {
  const clockedIn = !!status?.clocked_in;
  return (
    <div className="timeclock-punch">
      <div className="timeclock-name">{session.active_user.full_name}</div>
      <div className="timeclock-state">
        {clockedIn ? (
          <>
            <div className="timeclock-state-label">On the clock</div>
            <div className="timeclock-state-elapsed">{fmtElapsed(status.elapsed_seconds)}</div>
            {status.on_break && <div className="timeclock-state-break">ON BREAK</div>}
          </>
        ) : (
          <div className="timeclock-state-off">Not clocked in</div>
        )}
      </div>
      {err && <div className="pin-entry-error">{err}</div>}
      <div className="timeclock-actions">
        {!clockedIn && (
          <button className="timeclock-btn timeclock-btn-primary" disabled={busy} onClick={onClockIn}>Clock In</button>
        )}
        {clockedIn && !status?.on_break && (
          <>
            <button className="timeclock-btn timeclock-btn-primary" disabled={busy} onClick={onClockOut}>Clock Out</button>
            <button className="timeclock-btn" disabled={busy} onClick={onBreakStart}>Start Break</button>
          </>
        )}
        {clockedIn && status?.on_break && (
          <button className="timeclock-btn timeclock-btn-primary" disabled={busy} onClick={onBreakEnd}>End Break</button>
        )}
      </div>
      {!hideEndSession && onEndSession && (
        <button className="timeclock-cancel" onClick={onEndSession}>Done (next person)</button>
      )}
    </div>
  );
}

function ChecklistPunchModal({ scope, entryId, region, onClose }) {
  const [tmpl, setTmpl] = useState(null);
  const [responses, setResponses] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const blocking = scope === 'shift_end';

  useEffect(() => {
    api.listChecklistTemplates(scope, region).then(rows => setTmpl(rows[0] || null)).catch(e => setErr(e.message));
  }, [scope, region]);

  function toggle(id, checked) { setResponses(r => ({ ...r, [id]: { ...(r[id] || {}), checked } })); }
  function setNote(id, note) { setResponses(r => ({ ...r, [id]: { ...(r[id] || {}), note } })); }

  async function submit() {
    setBusy(true);
    setErr('');
    try {
      await api.completeChecklist({ template_id: tmpl.id, time_clock_entry_id: entryId, responses });
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const title = scope === 'shift_start' ? 'Start of Shift Checklist' : 'End of Shift Checklist';

  return (
    <Modal title={title} onClose={blocking ? () => {} : onClose}>
      {!tmpl && !err && <div>Loading…</div>}
      {err && <div className="form-error">{err}</div>}
      {tmpl && (
        <>
          <p className="checklist-intro">
            {scope === 'shift_start'
              ? 'Run through these before taking calls.'
              : 'Complete these before you leave.'}
          </p>
          <div className="checklist-items">
            {tmpl.items.map(item => (
              <div key={item.id} className="checklist-item">
                <label>
                  <input
                    type="checkbox"
                    checked={!!responses[item.id]?.checked}
                    onChange={e => toggle(item.id, e.target.checked)}
                  />
                  <span className="checklist-item-label">
                    {item.label}
                    {item.required && <span className="checklist-required">*</span>}
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Note (optional)"
                  className="checklist-note"
                  value={responses[item.id]?.note || ''}
                  onChange={e => setNote(item.id, e.target.value)}
                />
              </div>
            ))}
          </div>
          <div className="form-actions">
            {!blocking && <button type="button" className="btn-secondary" onClick={onClose}>Skip</button>}
            <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
              {busy ? 'Saving…' : 'Done'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
